import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  Workflow,
  WorkflowParentType,
  WorkflowRuleHistory,
  WorkflowRuleImplementation,
  WorkflowStatus,
  WorkflowStep,
  WorkflowStepAction
} from 'src/app/modules/core/store/models/workflow.model';
import { QueryParams, QueryResult } from '../../../core/store/models/query.model';
import { HttpService } from '../../../core/services/http.service';

import { API_BASE_URL as apiBaseUrl } from '@app/core/constants/api-base-url';

@Injectable({
  providedIn: 'root'
})
export class WorkflowApiService {
  constructor(private http: HttpService) {}

  public getWorkflowForDataset(datasetUId: string): Observable<QueryResult<Workflow>> {
    const params = {
      skip: 0,
      take: 1,
      where: `ParentUId=Guid.Parse("${datasetUId}")`,
      include: 'full'
    };

    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/meta/workflows?${queryString}`;

    return this.http.get<QueryResult<Workflow>>(url);
  }

  public updateWorkflow(workflow: Workflow): Observable<Workflow> {
    const url = `${apiBaseUrl}/v1/meta/workflows/${workflow.uId}?processChildData=true`;

    return this.http.put(url, workflow);
  }

  public getWorkflowStepActionsForStep(workflowUId: string, workflowStepUId: string): Observable<QueryResult<WorkflowStepAction>> {
    const params = {
      skip: 0,
      take: 100
    };

    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/meta/workflows/${workflowUId}/workflowSteps/${workflowStepUId}/workflowStepActions?${queryString}`;

    return this.http.get<QueryResult<Workflow>>(url);
  }

  public getWorkflowStepsForDeletion(workflowUId: string, workflowStepUId: string, datasetUId: string): Observable<WorkflowStep[]> {
    const url = `${apiBaseUrl}/v1/meta/workflows/${workflowUId}/workflowSteps/${workflowStepUId}/workflowStepsForDelete/${datasetUId}`;

    return this.http.get<WorkflowStep[]>(url);
  }

  // Workflow Rule Implementation
  public getRuleImplementations(parentType: WorkflowParentType): Observable<Array<WorkflowRuleImplementation>> {
    const url =
      parentType === WorkflowParentType.Dataset
        ? `${apiBaseUrl}/v1/data/ruleImplementations`
        : `${apiBaseUrl}/v1/notifications/ruleImplementations`;

    return this.http.get<Array<WorkflowRuleImplementation>>(url);
  }

  // Workflow Rule History
  public getWorkflowRuleHistory(datasetUId: string, documentUId: string): Observable<QueryResult<WorkflowRuleHistory>> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/workflow_rule/history_query/${documentUId}`;

    return this.http.post(url);
  }

  public getWorkflows(params: QueryParams, includeAll: boolean): Observable<QueryResult<Workflow>> {
    const request = {
      ...params,
      include: includeAll ? "full" : null
    };

    const queryString = this.http.queryStringFromObject(request);

    const url = `${apiBaseUrl}/v1/meta/workflows?${queryString}`;

    return this.http.get<QueryResult<Workflow>>(url);
  }

  public getFullWorkflow(workflowUId: string): Observable<QueryResult<Workflow>> {
    const params = {
      skip: 0,
      take: 1,
      where: `UId=Guid.Parse("${workflowUId}")`,
      include: 'full'
    };

    const queryString = this.http.queryStringFromObject(params);
    const url = `${apiBaseUrl}/v1/meta/workflows?${queryString}`;
    return this.http.get<QueryResult<Workflow>>(url);
  }

  public initiateWorkflow(workflow: Workflow): Observable<Workflow> {
    const url = `${apiBaseUrl}/v1/meta/workflows/init`;

    return this.http.post(url, workflow);
  }

  public createWorkflow(workflow: Workflow): Observable<Workflow> {
    const url = `${apiBaseUrl}/v1/meta/workflows`;

    return this.http.post(url, workflow);
  }

  public deleteWorkflow(uid: string): Observable<any> {
    const url = `${apiBaseUrl}/v1/meta/workflows/${uid}`;

    return this.http.delete(url);
  }

  public getWorkflowStatuses(params: QueryParams, includeTranslations: boolean): Observable<QueryResult<WorkflowStatus>> {
    const request = {
      ...params,
      includeTranslations: includeTranslations
    };

    const queryString = this.http.queryStringFromObject(request);

    const url = `${apiBaseUrl}/v1/meta/workflows/workflowStatuses?${queryString}`;

    return this.http.get<QueryResult<WorkflowStatus>>(url);
  }
}
